import React, { useState, useCallback } from "react";
import { api } from "../../../software/api";
import { Search, PackageSearch, ArrowRight, Loader2 } from "lucide-react";

const money = (n) => (n == null ? "" : `$${Number(n || 0).toFixed(2)}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU") : "—");

const SOURCE_TONE = {
  Equipment: "bg-sky-100 text-sky-700",
  "Purchase log": "bg-violet-100 text-violet-700",
};
const tone = (s) => SOURCE_TONE[s] || (s?.startsWith("Quote") ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700");

export default function WhoBought({ onOpenClient }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback(async (term) => {
    if (!term || term.trim().length < 2) { setData(null); setSearched(false); return; }
    setLoading(true); setSearched(true);
    try { setData(await api("GET", `/purchase-search?q=${encodeURIComponent(term.trim())}`)); }
    catch { setData({ count: 0, results: [] }); }
    finally { setLoading(false); }
  }, []);

  return (
    <div data-testid="who-bought-panel" className="max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <PackageSearch className="h-6 w-6 text-emerald-600" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Who Bought This?</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Find every client who bought a given spa, part or product — type a product name or SKU.</p>

      <form onSubmit={(e) => { e.preventDefault(); run(q); }} className="flex gap-2 mb-5">
        <div className="flex-1 min-w-0 flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2.5 bg-white">
          <Search className="h-5 w-5 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. spa pump, chlorine, sand filter…" data-testid="who-bought-input" className="flex-1 min-w-0 outline-none text-sm" />
        </div>
        <button type="submit" data-testid="who-bought-search" className="rounded-xl bg-emerald-600 text-white font-bold px-5 hover:bg-emerald-700">Search</button>
      </form>

      {loading ? (
        <p className="text-slate-500 py-10 text-center inline-flex items-center gap-2 justify-center w-full"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>
      ) : !searched ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="who-bought-idle">
          <PackageSearch className="h-10 w-10 mx-auto mb-2" />
          <p className="text-sm">Type a product above to see who owns it.</p>
        </div>
      ) : !data || data.count === 0 ? (
        <div className="rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="who-bought-empty">
          <p className="font-semibold">No buyers found for "{q}".</p>
          <p className="text-sm text-slate-400 mt-1">Note: 15-year legacy invoices/POS were imported as totals only, so only in-app sales, quotes, equipment &amp; purchase logs are searchable by product.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-2" data-testid="who-bought-count">{data.count} result{data.count === 1 ? "" : "s"}</p>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>
                <th className="text-left px-4 py-2.5">Client</th><th className="text-left px-4 py-2.5">Product</th>
                <th className="text-center px-4 py-2.5">Qty</th><th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Source</th><th className="text-right px-4 py-2.5">Price</th><th /></tr></thead>
              <tbody>
                {data.results.map((r, i) => (
                  <tr key={i} onClick={() => r.client_id && onOpenClient(r.client_id)} data-testid={`who-bought-row-${i}`} className={`border-t border-slate-100 ${r.client_id ? "hover:bg-emerald-50 cursor-pointer" : ""}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800 truncate max-w-[220px]">{r.client_name}</td>
                    <td className="px-4 py-2.5 text-slate-600 truncate max-w-[220px]">{r.product}</td>
                    <td className="px-4 py-2.5 text-center text-slate-500">{r.qty ?? "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone(r.source)}`}>{r.source}</span></td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{money(r.amount)}</td>
                    <td className="px-3 py-2.5 text-right">{r.client_id && <ArrowRight className="h-4 w-4 text-slate-300 inline" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
